import { ActivityItem, ChoiceGroup, DefaultButton, DirectionalHint, Dropdown, FontIcon, IActivityItemProps, IChoiceGroupOption, IDropdownOption, IPanelStyles, IPersonaProps, IPersonaSharedProps, IStackTokens, Label, mergeStyles, MessageBar, MessageBarButton, MessageBarType, Panel, PanelType, PrimaryButton, ResponsiveMode, ScrollablePane, ScrollbarVisibility, Spinner, SpinnerSize, Stack, StackItem, TextField, Toggle, TooltipHost } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { getEmailTemplateHtml } from "../../src/taskpane/components/helpers/shareFileEmailTemplateHelper";
import { Fileview } from "../../src/taskpane/components/_v2/components/Explorer/Fileview/Fileview";
import { IGraphCreateShareableLinkRequest, ShareableLinkRequestScope, ShareableLinkRequestType } from "../../src/taskpane/components/_v2/redux/api/graph/api-graph-interfaces";
import { useLazySendMailQuery } from "../../src/taskpane/components/_v2/redux/api/graph/api-graph-slice";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { PeopleFinder, ICustomPersonaProps } from "../../src/taskpane/components/_v2/_shared/PeopleFinder/PeopleFinder";
import { ComponentTypes } from "../../src/taskpane/config/config";
import { createItemShareableLink, getFileItemIdFromRelativePath, getItemWebViewUrl, getOrganizationVerifiedDomains } from "../../src/taskpane/services/GraphService";
import { createSharingLinkViaRest, getItemSharingInfo, ISharingLinkSettings, SharingLinkKind, SharingLinkScope, SharingRole } from "../../src/taskpane/services/SharePointRestServices";
import { formatSharedWithPrincipalLabel } from "../../src/taskpane/helpers/sharingPrincipalsHelper";
import { isMobile, mobileListItemStyle } from "../../src/taskpane/styles/mobileStyles";
import authModule from "../../utilities/AuthModule";
import generalModule from "../../utilities/GeneralModule";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { logger } from "../../utilities/logger";
import { IPageSettings } from "../../src/taskpane/interfaces/IPageSettings";
import { FileIconType, getFileTypeIconAsUrl, IFileTypeIconOptions } from "@fluentui/react-file-type-icons";
import { contentManager, SharePointFolderContentTypeIds } from "../../src/taskpane/config/constants";
import { IAuthAccount } from "../../src/taskpane/interfaces/IAuthTokenResultDto";
import { tryEncodeUriComponent } from "../../utilities/EncodeDecode";

const scrollStyle = mergeStyles({
    position: "relative",
    height: "calc(100vh - 160px)",
});

const truncatedFilenameStyle = mergeStyles({
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
});

const stackTokens: IStackTokens = { childrenGap: 20 };

const panelStyles: Partial<IPanelStyles> = {
    content: {
        paddingLeft: 15,
        paddingRight: 15
    }
}

export const enum SharePanelMode {
    Share,
    Copy
}

type CopyLinkItemResult = { item: any; link?: string; error?: string };

type CopyLinksBatch = {
    itemResults: CopyLinkItemResult[];
    hasFailedLinks: boolean;
    errorMessage: string;
};

type ShareMailResult = { ok: boolean; error?: string; filename?: string };

type ManualCopyItem = { item: any; link: string };

type CopyStatus = {
    manualItems: ManualCopyItem[] | null;
    manualCopyCompleted: boolean;
    manualCopyButtonFailed: boolean;
    manualCopyHasFailedLinks: boolean;
    copiedLinkCount: number;
};

const minItems = 5;
const maxConcurrentItemOperations = 5;
const initialCopyStatus: CopyStatus = {
    manualItems: null,
    manualCopyCompleted: false,
    manualCopyButtonFailed: false,
    manualCopyHasFailedLinks: false,
    copiedLinkCount: 0,
};

const normalizeRecipients = (recipients?: string[]): string[] => {
    return (recipients ?? [])
        .map(recipient => recipient?.trim())
        .filter((recipient): recipient is string => !!recipient);
};

async function mapItemsWithLimit<TResult>(
    items: any[],
    mapper: (item: any) => Promise<TResult>
): Promise<TResult[]> {
    const results: TResult[] = new Array(items.length);
    let nextIndex = 0;

    const worker = async () => {
        while (nextIndex < items.length) {
            const currentIndex = nextIndex;
            nextIndex += 1;
            results[currentIndex] = await mapper(items[currentIndex]);
        }
    };

    const workerCount = Math.min(maxConcurrentItemOperations, items.length);
    const workers: Promise<void>[] = [];
    for (let i = 0; i < workerCount; i++) {
        workers.push(worker());
    }

    await Promise.all(workers);
    return results;
}

const navigatorClipboardWriteText = (text: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        const textArea = document.createElement("textarea");
        try {
            textArea.value = text;
            textArea.setAttribute("readonly", "");
            textArea.style.position = "fixed";
            textArea.style.opacity = "0";
            document.body.appendChild(textArea);
            textArea.select();
            if (document.execCommand("copy")) {
                resolve();
            }
            else {
                reject(new Error("Clipboard copy was not accepted by the browser."));
            }
        } catch (e) {
            reject(e);
        } finally {
            if (textArea.parentNode) {
                textArea.parentNode.removeChild(textArea);
            }
        }
    });
}

export interface ICMFileSharePanelProps {
    panelIsOpen: boolean;
    closePanel: () => void;
    items: any[];
    mode: SharePanelMode;
    isContainer?: boolean;
    hideSharingInfo?: boolean;
    userAccount?: IAuthAccount;
}

export const CMFileSharePanel = ({ panelIsOpen, closePanel, items, mode, isContainer, hideSharingInfo, userAccount }: ICMFileSharePanelProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories?.oneDriveRootDetails);
    const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
    const user = useSelector((state: RootState) => state.user);

    const canCreateSharingLink = React.useMemo(() => !items?.some(x => x?.EffectiveBasePermissions?.canEditListItem === false), [items]);

    const [message, setMessage] = React.useState<string>('');
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [filenameWithoutExtention, setFilenameWithoutExtention] = React.useState<string>('');
    const [blockDownload, setBlockDownload] = React.useState(false);
    const [isCreatingLink, setIsCreatingLink] = React.useState(false);
    const [sharedSuccess, setSharedSuccess] = React.useState(false);
    const [copyStatus, setCopyStatus] = React.useState<CopyStatus>(initialCopyStatus);
    const copiedResetTimerRef = React.useRef<number | null>(null);
    const operationIdRef = React.useRef<number>(0);

    const clearCopiedResetTimer = React.useCallback(() => {
        if (copiedResetTimerRef.current) {
            window.clearTimeout(copiedResetTimerRef.current);
            copiedResetTimerRef.current = null;
        }
    }, []);

    React.useEffect(() => () => {
        operationIdRef.current += 1;
        clearCopiedResetTimer();
    }, [clearCopiedResetTimer]);

    React.useEffect(() => {
        if (!panelIsOpen) {
            operationIdRef.current += 1;
            setIsCreatingLink(false);
            setSharedSuccess(false);
            setErrorMessage("");
            setCopyStatus(initialCopyStatus);
            clearCopiedResetTimer();
        }
    }, [clearCopiedResetTimer, panelIsOpen]);

    const isCurrentOperation = React.useCallback((operationId: number): boolean => {
        return operationIdRef.current === operationId;
    }, []);

    const getActionableItems = React.useCallback((): any[] => {
        return (items ?? []).filter(item => !!item);
    }, [items]);

    const [types] = React.useState<IDropdownOption[]>([
        { key: 'edit', text: languageStrings.CanEditMakesChanges },
        { key: 'view', text: languageStrings.CanViewCannotMakeChanges }
    ]);
    const [selectedType, setSelectedType] = React.useState<IDropdownOption>(types[0]);

    const [scopes] = React.useState<IChoiceGroupOption[]>([
        // { key: ShareableLinkRequestScope.Anonymous, text: languageStrings.Anyone, disabled: true },
        { key: ShareableLinkRequestScope.Organization, text: languageStrings.MyOrganization },
        { key: ShareableLinkRequestScope.Users, text: languageStrings.PeopleYouChoose }
    ]);
    const [selectedScope, setSelectedScope] = React.useState<ShareableLinkRequestScope>(
        mode === SharePanelMode.Copy
            ? ShareableLinkRequestScope.Organization
            : ShareableLinkRequestScope.Users
    );

    const [activityItem, setActivityItem] = React.useState<IActivityItemProps>();
    const [tooltipContent, setTooltipContent] = React.useState<JSX.Element>();

    const [selectedPeopleDetails, setSelectedPeopleDetails] = React.useState<{ email: string; isGuest: boolean; isCustomEmail: boolean; isDirectoryUser: boolean }[]>([]);
    const selectedPeople = React.useMemo(() => selectedPeopleDetails.map(recipient => recipient.email), [selectedPeopleDetails]);

    // Whether the share target permits external/guest recipients (SharePoint's canAddExternalPrincipal).
    // undefined = not yet known — we FAIL OPEN and never block until it is explicitly false.
    const [canShareExternally, setCanShareExternally] = React.useState<boolean | undefined>(undefined);
    // Whether NEW external invites are allowed (SharePoint's canAddNewExternalPrincipal). false =
    // "Existing guests" mode — existing directory guests are allowed, but new/typed external are not.
    // undefined = unknown — FAIL OPEN (do not block new external until explicitly false).
    const [canInviteNewExternal, setCanInviteNewExternal] = React.useState<boolean | undefined>(undefined);
    // The tenant's verified domains (lower-cased) used to classify a recipient as internal vs external.
    const [orgVerifiedDomains, setOrgVerifiedDomains] = React.useState<string[]>([]);

    const selectedPersonasChanged = (selectedPersonas: ICustomPersonaProps[], customTextValue?: string) => {
        const details = selectedPersonas
            .filter(persona => persona?.secondaryText)
            .map(persona => ({
                email: persona.secondaryText.trim(),
                isGuest: persona.isGuest === true,
                isCustomEmail: persona.isCustomEmail === true,
                // Resolved from the directory (Graph /users or a directory-backed /me/people result) =>
                // already exists => an "existing guest" when external. Implicit contacts / typed emails are false.
                isDirectoryUser: persona.isDirectoryUser === true
            }));
        if (customTextValue?.trim()) {
            // A free-typed address is a NEW external invite, not a directory user; classified by domain, not the flag.
            details.push({ email: customTextValue.trim(), isGuest: true, isCustomEmail: true, isDirectoryUser: false });
        }
        // De-duplicate by email (case-insensitive) and drop empties — mirrors normalizeRecipients.
        const seen = new Set<string>();
        setSelectedPeopleDetails(details.filter(detail => {
            const key = detail.email.toLowerCase();
            if (!detail.email || seen.has(key)) return false;
            seen.add(key);
            return true;
        }));
    }

    // Classifies a selected recipient as external (outside the tenant).
    // - Custom typed addresses: the domain is the ONLY reliable signal (the picker forces isGuest=true on
    //   them), so a typed internal-domain address is correctly treated as internal.
    // - Resolved principals: the guest flag catches in-directory guests (incl. #EXT# accounts whose
    //   displayed domain can look internal); the domain check additionally catches guests whose userType
    //   was missing. When verified domains are unknown (lookup failed) we fall back to the guest flag only.
    const isExternalRecipient = React.useCallback((recipient: { email: string; isGuest: boolean; isCustomEmail: boolean; isDirectoryUser: boolean }): boolean => {
        const domain: string = recipient.email?.split("@")?.[1]?.toLowerCase()?.trim() ?? "";
        const domainIsExternal: boolean = orgVerifiedDomains.length > 0 && !!domain && !orgVerifiedDomains.includes(domain);
        return recipient.isCustomEmail ? domainIsExternal : (recipient.isGuest || domainIsExternal);
    }, [orgVerifiedDomains]);

    // Mirrors SharePoint's tenant "External file sharing" (SharingCapability) gate per recipient:
    //  - external sharing OFF ("Only people in your organization") -> block EVERY external recipient.
    //  - external ON but NEW invites OFF ("Existing guests")        -> block only NEW (typed) external;
    //    existing directory guests (resolved, not typed) stay allowed.
    //  - "New and existing guests" / "Anyone"                       -> nothing blocked here.
    // Unknown capability fails OPEN. Server-side policy stays the real enforcement; this is a UX pre-flight.
    const isBlockedRecipient = React.useCallback((recipient: { email: string; isGuest: boolean; isCustomEmail: boolean; isDirectoryUser: boolean }): boolean => {
        if (!isExternalRecipient(recipient)) return false;   // internal -> never blocked
        if (canShareExternally === false) return true;        // Disabled -> block all external
        // "Existing guests": block every external that is NOT already in the tenant directory.
        // SharePoint defines this capability as "people already in your directory", so the test is
        // EXISTENCE, not userType: a recipient resolved from the directory (Graph /users — which only
        // returns directory objects — or a directory-backed /me/people result) is an existing guest,
        // even when its userType comes back null. A typed raw email and a mailbox-only /me/people
        // ImplicitContact are NOT in the directory (isDirectoryUser false), so they correctly fall into
        // the blocked bucket, matching SharePoint which treats them as new external invites.
        const isExistingGuest: boolean = recipient.isDirectoryUser === true && recipient.isCustomEmail !== true;
        if (canInviteNewExternal === false && !isExistingGuest) return true;
        return false;
    }, [isExternalRecipient, canShareExternally, canInviteNewExternal]);

    const blockedExternalRecipients = React.useMemo(
        () => selectedScope === ShareableLinkRequestScope.Users
            ? selectedPeopleDetails.filter(isBlockedRecipient)
            : [],
        [selectedScope, selectedPeopleDetails, isBlockedRecipient]);
    const hasBlockedExternalRecipients: boolean = blockedExternalRecipients.length > 0;

    // Disabled => "only within org"; Existing-guests => "only existing guests".
    const externalBlockMessage: string = canShareExternally === false
        ? languageStrings.OnlyShareWithinOrganization
        : languageStrings.OnlyShareWithExistingGuests;

    const [sendMail] = useLazySendMailQuery();

    const isInOneDrive = React.useRef(currentDirectory?.type == ComponentTypes.OneDrive || currentDirectory?.type == ComponentTypes.OneDriveSubFolder);

    // Resolves the SharePoint identity (site URL + list GUID + item unique id) for an item, the same way
    // GetSharingInformation does. Shared by the external-sharing capability probe and the ShareLink REST fallback.
    const resolveSharePointIdentity = (item: any): { siteUrl?: string; listId?: string; uniqueId?: string; isItemInOneDrive: boolean } => {
        const isItemInOneDrive: boolean = item?.currentLocation
            ? (item.currentLocation.type == ComponentTypes.OneDrive || item.currentLocation.type == ComponentTypes.OneDriveSubFolder)
            : isInOneDrive.current;

        const siteUrl: string = isItemInOneDrive
            ? oneDriveRootDetails?.sharePointSiteUrl
            : (item?.currentLocation?.webUrl ?? currentDirectory?.webUrl);

        const listId: string = item?.sharePointListId
            || item?.sharepointIds?.listId
            || item?.["sharepointids"]?.listId
            || currentSharepointFolder?.sharepointListId;

        const uniqueId: string = item?.UniqueId
            ? item.UniqueId.replace("{", "").replace("}", "")
            : (item?.sharepointIds?.listItemUniqueId
                || item?.["listitemuniqueid"]
                || item?.["uniqueid"]);

        return { siteUrl, listId, uniqueId, isItemInOneDrive };
    };

    // Builds the "Shared with" tooltip/activity item from GetSharingInformation principals.
    // Behaviour preserved verbatim from the former standalone effect (sets only when principals exist).
    const applySharedWithTooltip = (principals: any[]) => {
        const personas: IPersonaSharedProps[] = (principals ?? []).map(principal => {
            let initials = "";

            // Initials come from the NAME only (not the email-suffixed label), so they stay "AV", not "A(".
            // Fall back to the email when a principal has no display name (e.g. an email-only link invitee),
            // so a missing name can't throw on .split(...).length and silently break the whole tooltip.
            const name: string = principal?.principal?.name ?? principal?.principal?.email ?? "";
            const parts = name.split(" ");

            if (parts.length === 1) {
                initials = `${parts[0].charAt(0)}`;
            }
            else if (parts.length > 1) {
                initials = `${parts[0].charAt(0)}${parts[1].charAt(0)}`;
            }

            // Individual users render as "Name (email)"; groups show their name only (see helper).
            const persona: IPersonaSharedProps = {
                text: formatSharedWithPrincipalLabel(principal?.principal),
                imageInitials: initials
            }
            return persona;
        });

        if (personas?.length > 0) {
            let activityDescription: any;
            if (personas.length === 1) {
                activityDescription = [<span key={1}>{personas[0].text}</span>]
            }
            else if (personas.length === 2) {
                activityDescription = [<span key={1}>{personas[0].text}, {personas[1].text}</span>]
            }
            else if (personas.length === 3) {
                activityDescription = [<span key={1}>{personas[0].text}, {personas[1].text} and 1 other</span>]
            }
            else if (personas.length > 3) {
                activityDescription = [<span key={1}>{personas[0].text}, {personas[1].text} and {personas.length - 2} others</span>]
            }

            setTooltipContent(
                <>
                    <Label>{languageStrings.SharedWith}</Label>
                    <ul>
                        {personas.map((persona, index) => {
                            return (
                                <li key={index}>{persona.text}</li>
                            )
                        })}
                    </ul>
                </>
            );

            setActivityItem({
                activityDescription: activityDescription,
                activityPersonas: personas,
                isCompact: true,
                // ActivityItem is one of two legacy v8 components whose styles fall
                // back to the GLOBAL light theme (getTheme()), bypassing ThemeProvider
                // entirely — so its colors never follow dark mode. Re-point the two
                // slots we render at theme tokens: the ring separating overlapped
                // coins must match the surface behind it (it reads as a gap), and the
                // description text tracks the muted foreground. Both tokens equal the
                // Fluent light defaults in light mode, so this only changes dark.
                styles: {
                    root: { color: "var(--db-fg-muted)" },
                    isCompactIcon: {
                        selectors: {
                            ".ms-Persona-imageArea": { borderColor: "var(--db-bg)" },
                        },
                    },
                },
            });
        }
    };

    // Load the tenant's verified domains once per open (used to classify recipients as external vs internal).
    React.useEffect(() => {
        if (!panelIsOpen) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const domains = await getOrganizationVerifiedDomains();
                if (!cancelled && Array.isArray(domains)) {
                    setOrgVerifiedDomains(domains);
                }
            } catch (error) {
                logger(error, "CMFileSharePanel.tsx (load verified domains)");
            }
        })();
        return () => { cancelled = true; };
    }, [panelIsOpen]);

    // Single GetSharingInformation fetch serving BOTH the external-sharing capability (any item count)
    // and the "shared with" tooltip (single item, not hidden) — previously two POSTs to the same endpoint.
    // Probed from the first actionable item; the capability is site-scoped so it represents the selection.
    // Capability resets to undefined when the panel closes so a stale value can never block a later open.
    React.useEffect(() => {
        if (!panelIsOpen) {
            setCanShareExternally(undefined);
            setCanInviteNewExternal(undefined);
            return undefined;
        }
        const actionableItems = (items ?? []).filter(Boolean);
        if (actionableItems.length === 0) return undefined;

        const item = actionableItems[0];
        const showSharedWith = !hideSharingInfo && actionableItems.length === 1;

        // Preserve the former tooltip effect's side-effect: when showing a single item's sharing info,
        // align isInOneDrive.current with that item's own location (it may live outside the current directory).
        if (showSharedWith && item.currentLocation) {
            isInOneDrive.current = item.currentLocation.type == ComponentTypes.OneDrive || item.currentLocation.type == ComponentTypes.OneDriveSubFolder;
        }

        let cancelled = false;
        (async () => {
            try {
                const { siteUrl, listId, uniqueId, isItemInOneDrive } = resolveSharePointIdentity(item);
                if (!siteUrl || !listId || !uniqueId) return;
                const accessToken = isItemInOneDrive
                    ? await authModule.getOneDriveAccessToken()
                    : await authModule.getSharePointAccessToken();

                const info = await getItemSharingInfo(accessToken, siteUrl, listId, uniqueId);
                if (cancelled) return;

                if (typeof info.canShareExternally === "boolean") {
                    setCanShareExternally(info.canShareExternally);
                }
                // undefined when the capability can't be determined -> fail open (no new-external block).
                setCanInviteNewExternal(info.canInviteNewExternal);

                if (showSharedWith) {
                    applySharedWithTooltip(info.principals);
                }
            } catch (error) {
                logger(error, "CMFileSharePanel.tsx (load sharing info)");
            }
        })();
        return () => { cancelled = true; };
    }, [panelIsOpen, items, hideSharingInfo]);

    const [showMoreIsClicked, setShowMore] = React.useState<boolean>(false);
    const [sectionStyle, setSectionStyle] = React.useState<any>();
    React.useEffect(() => {
        const itemHeight = isMobile ? mobileListItemStyle.height : 40;
        const itemCount = items?.length ?? 0;
        let containerHeight = itemHeight * itemCount;

        if (!showMoreIsClicked) {
            containerHeight = itemCount > minItems ? itemHeight * minItems : itemHeight * itemCount;
        }

        setSectionStyle(
            mergeStyles({
                maxHeight: containerHeight,
                overflow: "hidden",
                transition: "max-height 0.5s ease-in-out",
            })
        );
    }, [items, showMoreIsClicked]);

    const showMore = (items?.length ?? 0) > minItems && (
        <div
            className="xb-show-more-action"
            onClick={() => setShowMore((prevShowMoreIsClicked) => !prevShowMoreIsClicked)}
            style={{ marginTop: "5px" }}
        >
            <FontIcon iconName={!showMoreIsClicked ? "ChevronDown" : "ChevronUp"} />
            <span>{!showMoreIsClicked ? languageStrings.ShowMore : languageStrings.ShowLess}</span>
        </div>
    );

    React.useEffect(() => {
        if (mode === SharePanelMode.Copy) return;
        if (!isCreatingLink) return;

        const operationId = operationIdRef.current;

        (async () => {
            try {
                const actionableItems = getActionableItems();
                if (actionableItems.length === 0) {
                    if (isCurrentOperation(operationId)) {
                        setErrorMessage(languageStrings.ErrorMessageGeneric);
                        setSharedSuccess(false);
                    }
                    return;
                }

                const recipients = normalizeRecipients(selectedPeople);
                if (selectedScope === ShareableLinkRequestScope.Users && recipients.length === 0) {
                    if (isCurrentOperation(operationId)) {
                        setErrorMessage(languageStrings.PleaseSelectSomeoneShare);
                        setSharedSuccess(false);
                    }
                    return;
                }

                if (isCurrentOperation(operationId)) {
                    setErrorMessage("");
                }

                let sharedGraphTokenPromise: Promise<string> | undefined;
                const getSharedGraphToken = async (): Promise<string> => {
                    if (!sharedGraphTokenPromise) {
                        sharedGraphTokenPromise = authModule.getGraphAccessToken();
                    }
                    return sharedGraphTokenPromise;
                };

                // sendMail runs on the default Graph token: Mail.Send has been part of the sign-in
                // scopes since 2024-10 (verified in a live token's scp claim), so the separate
                // Mail.Send token flow is not needed here. Never reach Graph without a token:
                // "Bearer undefined" comes back as IDX14100 ("JWT is not well formed, there are no dots").
                const mailAccessToken = await getSharedGraphToken();
                if (!isCurrentOperation(operationId)) return;
                if (!mailAccessToken) {
                    setErrorMessage(languageStrings.ErrorMessageGeneric);
                    setSharedSuccess(false);
                    return;
                }

                const results = await mapItemsWithLimit(actionableItems, async item => sendShareMailForItem(item, recipients, getSharedGraphToken, mailAccessToken));

                if (!isCurrentOperation(operationId)) return;

                const anyFailed = results.some(r => !r.ok);
                const firstFilename = results.find(r => r.ok)?.filename ?? '';
                const aggregateError = results.find(r => r.error)?.error ?? languageStrings.ErrorMessageGeneric;

                setFilenameWithoutExtention(firstFilename);
                setErrorMessage(anyFailed ? aggregateError : '');
                setSharedSuccess(!anyFailed);
            } catch (error) {
                logger(error, "CMFileSharePanel.tsx (Share flow)");
                if (isCurrentOperation(operationId)) {
                    setErrorMessage(languageStrings.ErrorMessageGeneric);
                    setSharedSuccess(false);
                }
            } finally {
                if (isCurrentOperation(operationId)) {
                    setIsCreatingLink(false);
                }
            }
        })();
    }, [isCreatingLink])

    const getFileParts = (name?: string) => {
        let extention = '';
        let nameWithoutExtention = name || '';

        if (nameWithoutExtention) {
            const lastDotIndex = nameWithoutExtention.lastIndexOf('.');
            if (lastDotIndex > 0 && lastDotIndex < nameWithoutExtention.length - 1) {
                extention = nameWithoutExtention.substring(lastDotIndex + 1);
                nameWithoutExtention = nameWithoutExtention.substring(0, lastDotIndex);
            }
        }

        return {
            extention,
            nameWithoutExtention
        }
    }

    const getItemDisplayName = (item: any): string => {
        return item?.FileLeafRef ?? item?.name ?? '';
    };

    const getItemIconUrl = (item: any): string => {
        try {
            const itemContentTypeId: string = item?.ContentTypeId || item?.currentLocation?.contentTypeId || item?.["contenttypeid"];
            let iconType: IFileTypeIconOptions;

            if (itemContentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet)) {
                iconType = { type: FileIconType.docset, size: 16, imageFileType: "svg" };
            }
            else if (itemContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder)) {
                iconType = { type: FileIconType.folder, size: 16, imageFileType: "svg" };
            }
            else if (isContainer) {
                iconType = { type: FileIconType.folder, size: 16, imageFileType: "svg" };
            }
            else {
                const fileParts = getFileParts(getItemDisplayName(item));
                iconType = { extension: fileParts.extention?.trim(), size: 16, imageFileType: "svg" };
            }

            return getFileTypeIconAsUrl(iconType);
        } catch {
            return `${process.env.baseUri}/assets/icon-unknown.png`;
        }
    };

    const sendShareMailForItem = async (
        item: any,
        recipients: string[],
        getGraphToken: () => Promise<string>,
        mailAccessToken: string
    ): Promise<ShareMailResult> => {
        const fileParts = getFileParts(item?.FileLeafRef ?? item?.name);

        try {
            const result = await resolveItemShareLink(item, getGraphToken, recipients);

            if (result.error || !result.shareLink) {
                return { ok: false, error: result.error || languageStrings.ErrorMessageGeneric, filename: fileParts.nameWithoutExtention };
            }

            let iconUrl: string;
            try {
                let iconType: IFileTypeIconOptions;
                if (result.itemContentTypeId
                    && (result.itemContentTypeId.startsWith(SharePointFolderContentTypeIds.documentSet)
                        || result.itemContentTypeId.startsWith(SharePointFolderContentTypeIds.folder))
                ) {
                    if (result.itemContentTypeId.startsWith(SharePointFolderContentTypeIds.documentSet)) {
                        iconType = { type: FileIconType.docset, size: 48, imageFileType: "svg" };
                    }
                    else if (result.itemContentTypeId.startsWith(SharePointFolderContentTypeIds.folder)) {
                        iconType = { type: FileIconType.folder, size: 48, imageFileType: "svg" };
                    }
                }

                if (!iconType) {
                    if (isContainer) {
                        iconType = { type: FileIconType.folder, size: 48, imageFileType: "svg" };
                    }
                    else {
                        iconType = { extension: fileParts.extention?.trim(), size: 48, imageFileType: "svg" };
                    }
                }

                iconUrl = getFileTypeIconAsUrl(iconType);
            } catch (error) {
                iconUrl = `${process.env.baseUri}/assets/icon-unknown.png`;
            }

            const sharedByDisplayName = user?.account?.displayName ?? userAccount?.displayName ?? '';
            const sharedByText: string = (result.isFolderOrDocumentSet
                ? languageStrings.FolderSharedWithYouBy
                : languageStrings.DocumentSharedWithYouBy)
                .replace("[person]", sharedByDisplayName);

            const res = await sendMail({
                accessToken: mailAccessToken,
                html: getEmailTemplateHtml(
                    result.shareLink,
                    iconUrl,
                    sharedByText,
                    fileParts.nameWithoutExtention,
                    message,
                    languageStrings.Open
                ),
                subject: languageStrings.DocumentSharedWithYouByEmailSubject.replace("[person]", sharedByDisplayName).replace("[file_name]", fileParts.nameWithoutExtention),
                toRecipients: recipients
            });

            if (res.isError) {
                const mailError = res.error?.["data"]?.["error"]?.["message"] || languageStrings.ErrorMessageGeneric;
                logger(mailError, "CMFileSharePanel.tsx (sendMail)");
                return { ok: false, error: mailError, filename: fileParts.nameWithoutExtention };
            }

            return { ok: true, filename: fileParts.nameWithoutExtention };
        } catch (error) {
            logger(error, "CMFileSharePanel.tsx (sendShareMailForItem)");
            return { ok: false, error: languageStrings.ErrorMessageGeneric, filename: fileParts.nameWithoutExtention };
        }
    };

    // Maps the panel's currently selected sharing options to a SharePoint REST
    // `SharingLinkData` ("settings") payload. Kept identical in INTENT to the Graph
    // request built in resolveItemShareLink, so the REST fallback shares with the
    // exact same options the user picked.
    const buildSharePointShareLinkSettings = (recipientEmails: string[]): { settings: ISharingLinkSettings; peoplePickerInputs: string[] } => {
        // Permission (role) — mirrors the "Type" dropdown + the "Block download" toggle:
        //   Edit            -> SharingRole.Edit            (read / write)
        //   View            -> SharingRole.View            (read only)
        //   View + block dl -> SharingRole.RestrictedView  (view online; no download/print/copy)
        let role: SharingRole;
        if (selectedType.key === ShareableLinkRequestType.Edit) {
            role = SharingRole.Edit;
        } else if (blockDownload === true) {
            role = SharingRole.RestrictedView;
        } else {
            role = SharingRole.View;
        }

        // Audience (scope) — mirrors the "Share link with" choice:
        //   Organization -> only people in the tenant
        //   Anonymous    -> anyone with the link (needs tenant external-sharing enabled)
        //   Users        -> specific people (scoped via peoplePickerInput below)
        let scope: SharingLinkScope;
        let allowAnonymousAccess = false;
        if (selectedScope === ShareableLinkRequestScope.Organization) {
            scope = SharingLinkScope.Organization;
        } else if (selectedScope === ShareableLinkRequestScope.Anonymous) {
            scope = SharingLinkScope.Anyone;
            allowAnonymousAccess = true;
        } else {
            scope = SharingLinkScope.SpecificPeople;
        }

        // restrictShareMembership = true LOCKS the link to the explicitly invited people
        // (peoplePickerInput) so it cannot be redeemed by anyone else it is forwarded to.
        // This is what makes a "Specific people" link actually specific, and is exactly what
        // SharePoint's own Share dialog sends for that option (its default link type). The
        // Organization / Anyone links are intentionally open to their whole audience, so they
        // use false. Hence: true iff the audience is SpecificPeople.
        const restrictShareMembership: boolean = scope === SharingLinkScope.SpecificPeople;

        const settings: ISharingLinkSettings = {
            // Always Flexible: it honours `scope` + `role` and can express "view + block
            // download". The legacy kinds ignore `role` and so cannot represent every option.
            linkKind: SharingLinkKind.Flexible,
            scope,
            role,
            allowAnonymousAccess,
            restrictShareMembership,
            updatePassword: false
        };

        // For a specific-people link, restrict it to the chosen recipients via peoplePickerInput.
        // We return TWO candidate encodings to try in order, because how SharePoint resolves a
        // recipient differs by tenant / user type:
        //   1. Plain email in `Key`        — resolves for guests and many directory users.
        //   2. Login-name CLAIMS format    — `i:0#.f|membership|<email>` — required by some
        //      tenants for internal/org users that won't resolve from a bare email.
        // The caller tries #1 first and only falls back to #2 if #1 fails to produce a link
        // (see createShareLinkViaSharePointRest). The notification email itself is still sent by
        // the existing sendMail flow, not by ShareLink.
        const peoplePickerInputs: string[] = [];
        if (scope === SharingLinkScope.SpecificPeople && recipientEmails.length > 0) {
            peoplePickerInputs.push(
                JSON.stringify(recipientEmails.map(email => ({ Key: email, DisplayText: email }))),
                JSON.stringify(recipientEmails.map(email => ({ Key: `i:0#.f|membership|${email}`, DisplayText: email })))
            );
        }

        return { settings, peoplePickerInputs };
    };

    // SharePoint REST `ShareLink` fallback for a single item. Resolves the SharePoint
    // identity (site URL + list GUID + item unique id) the same way GetSharingInformation
    // does, acquires the matching SharePoint/OneDrive token (AllSites.Manage scope) and
    // mints the link. Returns undefined when the item can't be resolved or the call fails,
    // so the caller can fall back to surfacing the original error.
    const createShareLinkViaSharePointRest = async (item: any, recipientEmails: string[]): Promise<string | undefined> => {
        try {
            const { siteUrl, listId, uniqueId, isItemInOneDrive } = resolveSharePointIdentity(item);

            if (!siteUrl || !listId || !uniqueId) {
                return undefined;
            }

            const accessToken: string = isItemInOneDrive
                ? await authModule.getOneDriveAccessToken()
                : await authModule.getSharePointAccessToken();

            const { settings, peoplePickerInputs } = buildSharePointShareLinkSettings(recipientEmails);

            // Organization / Anyone links carry no people-picker input — a single attempt.
            if (peoplePickerInputs.length === 0) {
                const url: string | null = await createSharingLinkViaRest(accessToken, siteUrl, listId, uniqueId, settings);
                return url ?? undefined;
            }

            // Specific-people links: try each recipient encoding (plain email first, then the
            // i:0#.f|membership| claims format) and stop at the first that yields a link. This
            // recovers from tenants where a bare email fails to resolve to an internal/org user.
            for (let i = 0; i < peoplePickerInputs.length; i++) {
                const url: string | null = await createSharingLinkViaRest(accessToken, siteUrl, listId, uniqueId, settings, peoplePickerInputs[i]);
                if (url) {
                    return url;
                }
                if (i < peoplePickerInputs.length - 1) {
                    logger(new Error(`SharePoint ShareLink people-picker format #${i + 1} produced no link; retrying with the claims format.`), "CMFileSharePanel.tsx (createShareLinkViaSharePointRest)");
                }
            }
            return undefined;
        } catch (error) {
            logger(error, "CMFileSharePanel.tsx (createShareLinkViaSharePointRest)");
            return undefined;
        }
    };

    const resolveItemShareLink = async (item: any, getToken?: () => Promise<string>, recipients?: string[]): Promise<{
        shareLink?: string;
        itemContentTypeId?: string;
        isFolderOrDocumentSet?: boolean;
        error?: string;
    }> => {
        try {
            let cachedGraphTokenPromise: Promise<string> | undefined;
            const getGraphToken = async (): Promise<string> => {
                if (getToken) return getToken();
                if (!cachedGraphTokenPromise) {
                    cachedGraphTokenPromise = authModule.getGraphAccessToken();
                }
                return cachedGraphTokenPromise;
            };

            let driveId = '';
            let itemId = '';

            if (item.driveId && item.id) {
                driveId = item.driveId;
                itemId = item.id;
            }
            else if (item.driveId && item.itemId) {
                driveId = item.driveId;
                itemId = item.itemId;
            }
            else if (item.driveid && item["serverrelativepath"]) {
                driveId = item.driveid;
                itemId = item.itemid ?? await getFileItemIdFromRelativePath(await getGraphToken(), item.driveid, item["serverrelativepath"]);
            }
            else {
                const spItemUrlparts = item['.spItemUrl'].split('/');
                driveId = isInOneDrive.current ? oneDriveRootDetails?.driveId : (currentDirectory?.parentId ? currentDirectory?.parentId : currentDirectory?.id);
                itemId = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];
            }

            if (!itemId) {
                const driveUrl = new URL(await getItemWebViewUrl(await getGraphToken(), driveId, null));
                const folderUrl = new URL(driveUrl.origin + item['FileRef']);
                itemId = await getFileItemIdFromRelativePath(await getGraphToken(), driveId, folderUrl.pathname.replace(driveUrl.pathname, ""), false);
            }

            if (!itemId) {
                return { error: languageStrings.ErrorMessageGeneric };
            }

            let shareLink: string;
            if (canCreateSharingLink) {
                const effectiveRecipients = recipients ?? normalizeRecipients(selectedPeople);

                const request: IGraphCreateShareableLinkRequest = {
                    driveId,
                    itemId,
                    message,
                    type: selectedType.key === ShareableLinkRequestType.Edit
                        ? ShareableLinkRequestType.Edit
                        : blockDownload === true
                            ? ShareableLinkRequestType.BlocksDownload
                            : ShareableLinkRequestType.View,
                    scope: selectedScope
                };

                if (selectedScope === ShareableLinkRequestScope.Users) {
                    request.recipients = effectiveRecipients;
                }

                const createLinkResults = await createItemShareableLink(request, await getGraphToken());
                if (createLinkResults?.link?.webUrl) {
                    shareLink = createLinkResults.link.webUrl;
                }

                // Fallback: the Microsoft Graph /createLink (beta) endpoint can fail or return
                // no URL (tenant policy, throttling, transient beta-endpoint issues). When it
                // does, mint the link directly through the SharePoint REST `ShareLink` method
                // using the SAME selected options (type/block-download -> role, scope -> audience).
                if (!shareLink) {
                    shareLink = await createShareLinkViaSharePointRest(item, effectiveRecipients);
                }
            }
            else {
                shareLink = await getItemWebViewUrl(await getGraphToken(), driveId, itemId);
            }

            if (!shareLink) {
                logger(new Error(`Sharing link could not be created for request ${JSON.stringify({ type: selectedType.key === ShareableLinkRequestType.Edit ? ShareableLinkRequestType.Edit : blockDownload === true ? ShareableLinkRequestType.BlocksDownload : ShareableLinkRequestType.View, scope: selectedScope })}`), "CMFileSharePanel.tsx (resolveItemShareLink)");
                return { error: languageStrings.ErrorMessageGeneric };
            }

            const itemContentTypeId: string = item.ContentTypeId || item.currentLocation?.contentTypeId || item["contenttypeid"];
            let isFolderOrDocumentSet: boolean = isContainer === true;
            if (!isFolderOrDocumentSet && itemContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder)) {
                isFolderOrDocumentSet = true;
            }

            if (isFolderOrDocumentSet) {
                shareLink = `${process.env.contentManagerUrl}?${contentManager.params.preloadURL}=${tryEncodeUriComponent(shareLink)}`;
            }

            return { shareLink, itemContentTypeId, isFolderOrDocumentSet };
        } catch (error) {
            logger(error, "CMFileSharePanel.tsx (resolveItemShareLink)");
            return { error: languageStrings.ErrorMessageGeneric };
        }
    };

    const generateCopyLinksBatch = async (actionableItems: any[], recipients: string[]): Promise<CopyLinksBatch> => {
        let sharedGraphTokenPromise: Promise<string> | undefined;
        const getSharedToken = async (): Promise<string> => {
            if (!sharedGraphTokenPromise) {
                sharedGraphTokenPromise = authModule.getGraphAccessToken();
            }
            return sharedGraphTokenPromise;
        };

        const itemResults = await mapItemsWithLimit(actionableItems, async item => {
            const result = await resolveItemShareLink(item, getSharedToken, recipients);
            if (result.shareLink) {
                return { item, link: result.shareLink };
            }
            return { item, error: result.error || languageStrings.ErrorMessageGeneric };
        });

        return {
            itemResults,
            hasFailedLinks: itemResults.some(r => !!r.error),
            errorMessage: itemResults.find(r => r.error)?.error ?? "",
        };
    };

    const getSuccessfulLinks = (itemResults: CopyLinkItemResult[]): { item: any; link: string }[] =>
        itemResults.filter(r => !!r.link).map(r => ({ item: r.item, link: r.link! }));

    const handleClipboardFallback = async (batch: CopyLinksBatch | null, operationId: number) => {
        const successful = batch ? getSuccessfulLinks(batch.itemResults) : [];
        if (!batch || successful.length === 0) {
            if (isCurrentOperation(operationId)) {
                setErrorMessage(batch?.errorMessage || languageStrings.ErrorMessageGeneric);
                setSharedSuccess(false);
                setCopyStatus(initialCopyStatus);
            }
            return;
        }

        const text = successful.map(s => s.link).join("\r\n");

        try {
            await navigatorClipboardWriteText(text);
            if (!isCurrentOperation(operationId)) return;

            setCopyStatus({
                ...initialCopyStatus,
                copiedLinkCount: successful.length,
            });
            setSharedSuccess(!batch.hasFailedLinks);
            if (batch.hasFailedLinks) {
                setErrorMessage(batch.errorMessage || languageStrings.ErrorMessageGeneric);
            }
        } catch (error) {
            logger(error, "CMFileSharePanel.tsx (Clipboard execCommand fallback)");
            if (isCurrentOperation(operationId)) {
                setSharedSuccess(false);
                setCopyStatus({
                    ...initialCopyStatus,
                    manualItems: successful,
                    manualCopyHasFailedLinks: batch.hasFailedLinks,
                });
                if (batch.hasFailedLinks) {
                    setErrorMessage(batch.errorMessage || languageStrings.ErrorMessageGeneric);
                }
            }
        }
    };

    // MUST be invoked synchronously inside a user-gesture handler. Do NOT `await`
    // anything before calling this - Safari/WebKit require navigator.clipboard.write
    // to be reached without any awaits after the gesture, or it throws NotAllowedError.
    const runCopyMode = (actionableItems: any[], recipients: string[], operationId: number) => {
        setIsCreatingLink(true);

        const linksResultPromise = generateCopyLinksBatch(actionableItems, recipients);

        const canUsePromiseClipboard =
            typeof ClipboardItem !== 'undefined'
            && !!navigator.clipboard?.write
            && typeof window !== 'undefined'
            && window.isSecureContext;

        let clipboardWritePromise: Promise<void> | null = null;
        if (canUsePromiseClipboard) {
            try {
                const blobPromise = linksResultPromise.then(batch => {
                    const successful = getSuccessfulLinks(batch.itemResults);
                    if (successful.length === 0) {
                        throw new Error(batch.errorMessage || languageStrings.ErrorMessageGeneric);
                    }
                    return new Blob([successful.map(s => s.link).join("\r\n")], { type: 'text/plain' });
                });

                // Constructed and invoked synchronously so the click's user-gesture activation is preserved.
                // Safari / WebKit require clipboard.write to be reached without any awaits after the gesture.
                clipboardWritePromise = navigator.clipboard.write([new ClipboardItem({ 'text/plain': blobPromise })]);
            } catch (error) {
                // ClipboardItem constructor or clipboard.write can throw synchronously on browsers with partial
                // support (older Firefox, some WebViews). Fall through to execCommand in the same tick so we
                // do not lose user-gesture activation.
                logger(error, "CMFileSharePanel.tsx (ClipboardItem construct sync throw)");
                clipboardWritePromise = null;
            }
        }

        if (clipboardWritePromise) {
            (async () => {
                try {
                    await clipboardWritePromise;
                    const batch = await linksResultPromise;
                    if (!isCurrentOperation(operationId)) return;

                    const successful = getSuccessfulLinks(batch.itemResults);
                    if (successful.length === 0) {
                        setErrorMessage(batch.errorMessage || languageStrings.ErrorMessageGeneric);
                        setSharedSuccess(false);
                        setCopyStatus(initialCopyStatus);
                        return;
                    }

                    setCopyStatus({
                        ...initialCopyStatus,
                        copiedLinkCount: successful.length,
                    });
                    setSharedSuccess(!batch.hasFailedLinks);
                    if (batch.hasFailedLinks) {
                        setErrorMessage(batch.errorMessage || languageStrings.ErrorMessageGeneric);
                    }
                } catch (error) {
                    logger(error, "CMFileSharePanel.tsx (Clipboard write ClipboardItem)");
                    const batch = await linksResultPromise.catch(() => null);
                    await handleClipboardFallback(batch, operationId);
                } finally {
                    if (isCurrentOperation(operationId)) {
                        setIsCreatingLink(false);
                    }
                }
            })();
        }
        else {
            (async () => {
                try {
                    const batch = await linksResultPromise.catch(() => null);
                    await handleClipboardFallback(batch, operationId);
                } finally {
                    if (isCurrentOperation(operationId)) {
                        setIsCreatingLink(false);
                    }
                }
            })();
        }
    };

    const copyAllManualLinks = async () => {
        const manualItems = copyStatus.manualItems;
        if (!manualItems || manualItems.length === 0) return;
        const operationId = operationIdRef.current;
        const text = manualItems.map(e => e.link).join("\r\n");
        try {
            if (!generalModule.getIsOfficeJSAvailable() && navigator.clipboard?.writeText && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
            } else {
                await navigatorClipboardWriteText(text);
            }
            if (!isCurrentOperation(operationId)) return;

            if (!copyStatus.manualCopyHasFailedLinks) {
                setCopyStatus({
                    ...initialCopyStatus,
                    copiedLinkCount: manualItems.length,
                });
                setSharedSuccess(true);
                setErrorMessage("");
                return;
            }
            setCopyStatus(prev => ({
                ...prev,
                copiedLinkCount: manualItems.length,
                manualCopyCompleted: true,
                manualCopyButtonFailed: false,
            }));
            clearCopiedResetTimer();
            copiedResetTimerRef.current = window.setTimeout(() => {
                if (isCurrentOperation(operationId)) {
                    setCopyStatus(prev => ({
                        ...prev,
                        manualCopyCompleted: false,
                    }));
                    copiedResetTimerRef.current = null;
                }
            }, 2000);
        } catch (error) {
            logger(error, "CMFileSharePanel.tsx (copyAllManualLinks)");
            if (isCurrentOperation(operationId)) {
                setCopyStatus(prev => ({
                    ...prev,
                    manualCopyCompleted: false,
                    manualCopyButtonFailed: true,
                }));
            }
        }
    };

    const createShareableLink = async () => {
        operationIdRef.current += 1;
        const operationId = operationIdRef.current;
        const actionableItems = getActionableItems();
        const recipients = normalizeRecipients(selectedPeople);

        setSharedSuccess(false);
        setCopyStatus(initialCopyStatus);
        clearCopiedResetTimer();
        setErrorMessage("");

        if (actionableItems.length === 0) {
            setErrorMessage(languageStrings.ErrorMessageGeneric);
            return;
        }

        if (selectedScope === ShareableLinkRequestScope.Users && recipients.length === 0) {
            setErrorMessage(languageStrings.PleaseSelectSomeoneShare);
            return;
        }

        // Defence in depth behind the disabled button: never attempt a share the tenant forbids.
        if (hasBlockedExternalRecipients) {
            setErrorMessage(externalBlockMessage);
            return;
        }

        if (mode === SharePanelMode.Copy) {
            runCopyMode(actionableItems, recipients, operationId);
            return;
        }

        // The share effect acquires the Graph token itself; no separate Mail.Send consent step.
        setIsCreatingLink(true);
    }

    const dismissPanel = () => {
        operationIdRef.current += 1;
        clearCopiedResetTimer();
        closePanel();
    };

    const onRenderFooterContent = () => (
        <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 15 }}>
            {!sharedSuccess && <PrimaryButton
                iconProps={mode === SharePanelMode.Copy
                    ? { iconName: "Link" }
                    : { iconName: "Send" }}
                onClick={() => {
                    createShareableLink();
                }}
                disabled={isCreatingLink || hasBlockedExternalRecipients}
                text={mode === SharePanelMode.Copy
                    ? languageStrings.CopyLink
                    : languageStrings.Send}
            >
                {(isCreatingLink) && <Spinner size={SpinnerSize.small} styles={{ root: { paddingLeft: 5 } }} />}
            </PrimaryButton>}
            {sharedSuccess && <DefaultButton
                iconProps={{ iconName: "Checkmark" }}
                onClick={() => {
                    dismissPanel();
                }}
                text={languageStrings.Close}
            />}
        </Stack>
    );

    const onTypeChange = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
        setSelectedType(item);
    };

    const onScopeChange = (ev: React.FormEvent<HTMLInputElement>, option: IChoiceGroupOption): void => {
        setSelectedScope(option.key as ShareableLinkRequestScope);
    }

    return (
        <Panel
            isOpen={panelIsOpen}
            headerText={languageStrings.Share}
            onDismiss={dismissPanel}
            isLightDismiss
            closeButtonAriaLabel="Close"
            type={PanelType.custom}
            customWidth={!generalModule.isInConentMangerMode() ? "300px" : "450px"}
            styles={panelStyles}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            <div className={scrollStyle} >
                <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                    <Stack tokens={stackTokens} style={{ marginTop: '15px' }}>
                        {errorMessage && <MessageBar messageBarType={MessageBarType.error} isMultiline={true} dismissButtonAriaLabel="Close" onDismiss={() => setErrorMessage("")}>
                            {errorMessage}
                        </MessageBar>}
                        {sharedSuccess && <MessageBar messageBarType={MessageBarType.success} isMultiline={false} dismissButtonAriaLabel="Close">
                            {mode === SharePanelMode.Share
                                ? languageStrings.DocumentShareSuccess.replace("[file_name]", filenameWithoutExtention)
                                : `${copyStatus.copiedLinkCount} ${languageStrings.SharedLinkSuccessfullyCopied}`}
                        </MessageBar>}
                        {copyStatus.manualItems && copyStatus.manualItems.length > 0 && (
                            <MessageBar
                                messageBarType={MessageBarType.warning}
                                isMultiline={true}
                                actions={
                                    <div>
                                        <MessageBarButton
                                            iconProps={{ iconName: copyStatus.manualCopyCompleted ? "Checkmark" : "Copy" }}
                                            onClick={copyAllManualLinks}
                                            ariaLabel={copyStatus.manualCopyCompleted ? languageStrings.Copied : languageStrings.CopyLink}
                                        >
                                            {copyStatus.manualCopyCompleted ? languageStrings.Copied : languageStrings.CopyLink}
                                        </MessageBarButton>
                                    </div>
                                }
                            >
                                <Stack tokens={{ childrenGap: 8 }}>
                                    <span>
                                        {copyStatus.manualCopyButtonFailed
                                            ? languageStrings.ClipboardBlockedInstructions
                                            : languageStrings.ManualClipboardCopyInstructions}
                                    </span>
                                    <Stack tokens={{ childrenGap: 4 }}>
                                        {copyStatus.manualItems.map((manualItem, idx) => {
                                            const filename = getItemDisplayName(manualItem.item);
                                            const iconUrl = getItemIconUrl(manualItem.item);
                                            return (
                                                <Stack
                                                    key={idx}
                                                    horizontal
                                                    verticalAlign="center"
                                                    tokens={{ childrenGap: 8 }}
                                                    styles={{ root: { minWidth: 0 } }}
                                                >
                                                    <img src={iconUrl} alt="" width={16} height={16} style={{ flexShrink: 0 }} />
                                                    <Stack.Item grow styles={{ root: { minWidth: 0 } }}>
                                                        <TooltipHost content={filename} directionalHint={DirectionalHint.topLeftEdge}>
                                                            <div className={truncatedFilenameStyle}>{filename}</div>
                                                        </TooltipHost>
                                                    </Stack.Item>
                                                </Stack>
                                            );
                                        })}
                                    </Stack>
                                    {copyStatus.manualCopyButtonFailed && (
                                        <TextField
                                            ariaLabel={languageStrings.CopyLink}
                                            value={copyStatus.manualItems.map(e => e.link).join("\r\n")}
                                            multiline
                                            readOnly
                                            rows={Math.min(Math.max(copyStatus.manualItems.length, 2), 6)}
                                        />
                                    )}
                                </Stack>
                            </MessageBar>
                        )}
                        <div className={sectionStyle}>
                            {items?.map((item, index) => (
                                <div key={index}>
                                    <Fileview
                                        item={item}
                                        isMobile={isMobile}
                                        multiSelect={false}
                                        hasShowMore={false}
                                        tenantEnablePII={false}
                                        nonClickable={true}
                                    />
                                </div>
                            ))}
                        </div>
                        {showMore}
                        {mode === SharePanelMode.Copy && <ChoiceGroup label={languageStrings.ShareLinkWith} defaultSelectedKey={selectedScope} options={scopes} onChange={onScopeChange} />}
                        {selectedScope === ShareableLinkRequestScope.Users && <PeopleFinder label={languageStrings.ShareWithPeople} allowExternalUsers={canShareExternally !== false} selectedPersonasChanged={selectedPersonasChanged} />}
                        {hasBlockedExternalRecipients && <MessageBar messageBarType={MessageBarType.blocked} isMultiline={true}>{externalBlockMessage}</MessageBar>}
                        {mode === SharePanelMode.Share && <TextField label={languageStrings.Message} value={message} multiline rows={5} onChange={(event, newValue) => setMessage(newValue)} />}
                        {canCreateSharingLink && <Dropdown
                            label={languageStrings.Type}
                            options={types}
                            selectedKey={selectedType.key}
                            onChange={onTypeChange}
                        />}
                        {canCreateSharingLink && selectedType.key === ShareableLinkRequestType.View && <Toggle label={languageStrings.BlockDownload} checked={blockDownload} onText={languageStrings.Yes} offText={languageStrings.No} onChange={() => {
                            setBlockDownload(prev => !prev);
                        }} />}
                        {activityItem && <StackItem>
                            <Label>{languageStrings.SharedWith}</Label>
                            <TooltipHost content={tooltipContent} directionalHint={DirectionalHint.topLeftEdge}>
                                <ActivityItem {...(activityItem as IActivityItemProps)} />
                            </TooltipHost>
                        </StackItem>}
                    </Stack>
                </ScrollablePane>
            </div>
        </Panel>
    )
};
